import React, { useState, useEffect } from 'react';

const Preloader = ({ onComplete }) => {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const exitTimer = setTimeout(() => setExiting(true), 3900);
    const doneTimer = setTimeout(() => onComplete(), 4500);
    return () => { clearTimeout(exitTimer); clearTimeout(doneTimer); };
  }, [onComplete]);

  return (
    <div className={`preloader-screen ${exiting ? 'exiting' : ''}`} data-testid="preloader">
      <div className="preloader-svg-wrap">
        {/* Background ambient glows */}
        <div className="loader-glow-brain" style={{ top: '54%', left: '50%', transform: 'translate(-50%,-50%)' }} />
        <div className="loader-glow-tree" style={{ top: '32%', left: '50%', transform: 'translate(-50%,-50%)' }} />

        {/* Orbital rings - HTML divs for CSS 3D */}
        <div className="orbit-anchor" style={{ top: '52%', left: '50%' }}>
          <div className="orbit-ring">
            <div className="orbit-dot" />
            <div className="orbit-dot-2" />
          </div>
          <div className="orbit-ring-2" />
        </div>

        {/* SVG Brain-Tree Animation */}
        <svg className="brain-tree-svg" viewBox="0 0 400 440" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="softGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <radialGradient id="leafGrad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#8DA399" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#2A9D8F" stopOpacity="0.5" />
            </radialGradient>
          </defs>

          {/* ---- BRAIN ---- */}
          {/* Left hemisphere */}
          <path
            pathLength="1"
            className="loader-brain-l"
            d="M 200 278 C 188 267 172 260 156 262 C 134 265 118 278 113 296 C 108 314 113 332 126 343 C 139 354 156 358 170 354 C 183 350 192 340 196 330"
            stroke="#2A9D8F"
            strokeWidth="1.8"
            strokeLinecap="round"
            filter="url(#softGlow)"
          />
          {/* Right hemisphere */}
          <path
            pathLength="1"
            className="loader-brain-r"
            d="M 200 278 C 212 267 228 260 244 262 C 266 265 282 278 287 296 C 292 314 287 332 274 343 C 261 354 244 358 230 354 C 217 350 208 340 204 330"
            stroke="#2A9D8F"
            strokeWidth="1.8"
            strokeLinecap="round"
            filter="url(#softGlow)"
          />
          {/* Center fissure (longitudinal) */}
          <path
            pathLength="1"
            className="loader-fissure"
            d="M 200 278 L 200 330"
            stroke="#2A9D8F"
            strokeWidth="1"
            strokeOpacity="0.3"
            strokeDasharray="3 4"
          />
          {/* Left gyri */}
          <path
            pathLength="1"
            className="loader-gyrus"
            d="M 155 286 C 148 296 148 312 155 320"
            stroke="#8DA399"
            strokeWidth="1.2"
            strokeOpacity="0.5"
            strokeLinecap="round"
          />
          <path
            pathLength="1"
            className="loader-gyrus-2"
            d="M 170 278 C 163 288 163 304 170 312"
            stroke="#8DA399"
            strokeWidth="1.1"
            strokeOpacity="0.4"
            strokeLinecap="round"
          />
          {/* Right gyri */}
          <path
            pathLength="1"
            className="loader-gyrus-3"
            d="M 245 286 C 252 296 252 312 245 320"
            stroke="#8DA399"
            strokeWidth="1.2"
            strokeOpacity="0.5"
            strokeLinecap="round"
          />
          <path
            pathLength="1"
            className="loader-gyrus-4"
            d="M 230 278 C 237 288 237 304 230 312"
            stroke="#8DA399"
            strokeWidth="1.1"
            strokeOpacity="0.4"
            strokeLinecap="round"
          />
          {/* Neural nodes inside brain */}
          <circle cx="164" cy="300" r="2" fill="#2A9D8F" className="loader-node" />
          <circle cx="178" cy="290" r="1.5" fill="#2A9D8F" className="loader-node-2" />
          <circle cx="183" cy="312" r="2" fill="#2A9D8F" className="loader-node-3" />
          <circle cx="236" cy="300" r="2" fill="#2A9D8F" className="loader-node-4" />
          <circle cx="222" cy="290" r="1.5" fill="#2A9D8F" className="loader-node-5" />
          <circle cx="217" cy="312" r="2" fill="#2A9D8F" className="loader-node-6" />

          {/* ---- TREE ---- */}
          {/* Main trunk */}
          <path
            pathLength="1"
            className="loader-trunk"
            d="M 200 278 C 200 258 200 228 200 168"
            stroke="#2A9D8F"
            strokeWidth="3"
            strokeLinecap="round"
            filter="url(#softGlow)"
          />
          {/* Level 1 branches */}
          <path
            pathLength="1"
            className="loader-branch-l1"
            d="M 200 190 C 186 182 168 175 148 162"
            stroke="#2A9D8F"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            pathLength="1"
            className="loader-branch-r1"
            d="M 200 190 C 214 182 232 175 252 162"
            stroke="#2A9D8F"
            strokeWidth="2"
            strokeLinecap="round"
          />
          {/* Level 2 branches */}
          <path
            pathLength="1"
            className="loader-branch-l2"
            d="M 200 218 C 181 208 158 200 132 185"
            stroke="#8DA399"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            pathLength="1"
            className="loader-branch-r2"
            d="M 200 218 C 219 208 242 200 268 185"
            stroke="#8DA399"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          {/* Top sub-branches from trunk apex */}
          <path
            pathLength="1"
            className="loader-sub-top-l"
            d="M 200 168 C 196 155 192 142 188 128"
            stroke="#2A9D8F"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          <path
            pathLength="1"
            className="loader-sub-top-r"
            d="M 200 168 C 204 155 208 142 212 128"
            stroke="#2A9D8F"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          {/* Sub-branches from L1 */}
          <path
            pathLength="1"
            className="loader-sub-l1a"
            d="M 148 162 C 138 152 126 140 112 128"
            stroke="#2A9D8F"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          <path
            pathLength="1"
            className="loader-sub-l1b"
            d="M 148 162 C 140 150 130 138 122 124"
            stroke="#2A9D8F"
            strokeWidth="1.1"
            strokeLinecap="round"
          />
          {/* Sub-branches from R1 */}
          <path
            pathLength="1"
            className="loader-sub-r1a"
            d="M 252 162 C 262 152 274 140 288 128"
            stroke="#2A9D8F"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          <path
            pathLength="1"
            className="loader-sub-r1b"
            d="M 252 162 C 260 150 270 138 278 124"
            stroke="#2A9D8F"
            strokeWidth="1.1"
            strokeLinecap="round"
          />
          {/* Sub-branches from L2 */}
          <path
            pathLength="1"
            className="loader-sub-l2"
            d="M 132 185 C 118 174 103 162 88 148"
            stroke="#8DA399"
            strokeWidth="1"
            strokeLinecap="round"
          />
          {/* Sub-branches from R2 */}
          <path
            pathLength="1"
            className="loader-sub-r2"
            d="M 268 185 C 282 174 297 162 312 148"
            stroke="#8DA399"
            strokeWidth="1"
            strokeLinecap="round"
          />

          {/* ---- LEAVES ---- */}
          <circle cx="112" cy="128" r="5" fill="url(#leafGrad)" className="loader-leaf loader-leaf-1" />
          <circle cx="122" cy="124" r="4" fill="#8DA399" fillOpacity="0.7" className="loader-leaf loader-leaf-2" />
          <circle cx="288" cy="128" r="5" fill="url(#leafGrad)" className="loader-leaf loader-leaf-3" />
          <circle cx="278" cy="124" r="4" fill="#8DA399" fillOpacity="0.7" className="loader-leaf loader-leaf-4" />
          <circle cx="88" cy="148" r="4.5" fill="#2A9D8F" fillOpacity="0.65" className="loader-leaf loader-leaf-5" />
          <circle cx="312" cy="148" r="4.5" fill="#2A9D8F" fillOpacity="0.65" className="loader-leaf loader-leaf-6" />
          <circle cx="188" cy="128" r="3.5" fill="#8DA399" fillOpacity="0.75" className="loader-leaf loader-leaf-7" />
          <circle cx="212" cy="128" r="3.5" fill="#8DA399" fillOpacity="0.75" className="loader-leaf loader-leaf-8" />
          <circle cx="104" cy="118" r="3" fill="#2A9D8F" fillOpacity="0.5" className="loader-leaf loader-leaf-9" />
          <circle cx="296" cy="118" r="3" fill="#2A9D8F" fillOpacity="0.5" className="loader-leaf loader-leaf-10" />

          {/* ---- FLOATING PARTICLES ---- */}
          <circle cx="342" cy="200" r="2" fill="#F4A261" fillOpacity="0.55" className="loader-particle loader-particle-1" />
          <circle cx="60" cy="220" r="1.5" fill="#2A9D8F" fillOpacity="0.45" className="loader-particle loader-particle-2" />
          <circle cx="355" cy="280" r="1.2" fill="#8DA399" fillOpacity="0.5" className="loader-particle loader-particle-3" />
          <circle cx="48" cy="300" r="1.5" fill="#F4A261" fillOpacity="0.4" className="loader-particle loader-particle-4" />
        </svg>
      </div>

      {/* Brand name */}
      <div className="loader-brand-wrap">
        <img
          src="/assets/mindvrik-logo-transparent.png"
          alt="MindVrik"
          data-testid="loader-brand-name"
          className="loader-logo-image"
        />
        <p className="loader-tagline">Guided Intelligence. Human Growth.</p>
      </div>
    </div>
  );
};

export default Preloader;
